import React, { useMemo } from "react";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import {
  BENCHMARK_INSTITUTION_COLORS,
  formatBenchmarkChartValue,
} from "../../benchmarkMetricConfig";
import styles from "./CitationByYearChart.module.scss";

// Higher-contrast blue/red palette inspired by the keyword cloud tones.
const CITATION_LINE_COLORS = [
  "#1f4e8c",
  "#b23a48",
  "#2f6fa3",
  "#c44f5e",
  "#1e5f74",
  "#9f3f52",
  "#356fa8",
  "#b54a5a",
];

/**
 * Plots one line per selected entity using the `citation_by_year` map
 * surfaced from the researcher/unit/venue statistics endpoints.
 *
 * `series` is the output of `collectMetricByYearSeries`:
 *   [{ id, name, photo_url, points: [{ year, value }] }]
 */
const CitationByYearChart = ({
  series,
  entityLabel = "Entity",
  title,
  subtitle,
  colorMap,
  // Export mode: fill the parent's exact height and disable animation so an
  // off-screen snapshot renders fully (no mid-reveal clipping).
  exportMode = false,
}) => {
  const chartData = useMemo(() => {
    if (!series?.length) return [];
    const yearSet = new Set();
    series.forEach(({ points }) =>
      (points || []).forEach((p) => yearSet.add(p.year))
    );
    const years = Array.from(yearSet).sort((a, b) => a - b);
    return years.map((year) => {
      const row = { year };
      series.forEach(({ id, points }) => {
        const match = points.find((p) => p.year === year);
        row[id] = match ? match.value : null;
      });
      return row;
    });
  }, [series]);

  if (!series?.length || !chartData.length) return null;

  return (
    <section
      className={styles.card}
      aria-labelledby="citation-by-year-title"
      style={
        exportMode
          ? { height: "100%", display: "flex", flexDirection: "column" }
          : undefined
      }
    >
      <div className={styles.head} data-report-strip="true">
        <h3 id="citation-by-year-title" className={styles.title}>
          {title ?? "Citations over time"}
        </h3>
        <p className={styles.subtitle}>
          {subtitle ??
            `Annual citation counts for the selected ${entityLabel.toLowerCase()}${series.length > 1 ? "s" : ""}.`}
        </p>
      </div>
      <div
        className={styles["chart-area"]}
        style={exportMode ? { flex: 1, minHeight: 0 } : undefined}
      >
        <ResponsiveContainer width="100%" height={exportMode ? "100%" : 340}>
          <LineChart
            data={chartData}
            margin={{ top: 12, right: 24, bottom: 8, left: 8 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(15,52,96,0.08)" />
            <XAxis
              dataKey="year"
              stroke="#64748b"
              fontSize={12}
              tickFormatter={(y) => String(y)}
            />
            <YAxis
              stroke="#64748b"
              fontSize={12}
              tickFormatter={(v) => formatBenchmarkChartValue(v, 0)}
            />
            <Tooltip
              formatter={(value, _name, ctx) => [
                formatBenchmarkChartValue(value, 0),
                series.find((s) => s.id === ctx.dataKey)?.name || ctx.dataKey,
              ]}
              labelFormatter={(year) => `Year ${year}`}
              contentStyle={{
                borderRadius: 10,
                border: "1px solid rgba(0,0,0,0.08)",
                boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
              }}
            />
            <Legend
              verticalAlign="bottom"
              wrapperStyle={{ fontSize: 12 }}
              formatter={(value) =>
                series.find((s) => s.id === value)?.name || value
              }
            />
            {series.map((entity, idx) => (
              <Line
                key={entity.id}
                type="monotone"
                dataKey={entity.id}
                name={entity.id}
                stroke={
                  colorMap?.[entity.id] ||
                  CITATION_LINE_COLORS[idx % CITATION_LINE_COLORS.length] ||
                  BENCHMARK_INSTITUTION_COLORS[
                    idx % BENCHMARK_INSTITUTION_COLORS.length
                  ]
                }
                strokeWidth={2.5}
                dot={{ r: 3, strokeWidth: 1.5, fill: "#ffffff" }}
                activeDot={{ r: 5, strokeWidth: 1.5, fill: "#ffffff" }}
                connectNulls
                isAnimationActive={!exportMode}
              />
            ))}
          </LineChart>
        </ResponsiveContainer>
      </div>
    </section>
  );
};

export default CitationByYearChart;
